import type { ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { cn } from "@/lib/utils";
import { DURATION, EASE_SIGNATURE } from "./motion";
import { IconBubble } from "./IconBubble";
import { Tile, type Domain } from "./Tile";

interface DeviceTileProps {
  name: string;
  icon: ReactNode;
  domain?: Domain;
  /** Human-readable state, e.g. "On", "72%", "21.5 C". */
  state: string;
  /** Active = on/engaged. Drives the accent wash. */
  active?: boolean;
  /** Unavailable devices render dimmed and non-interactive. */
  available?: boolean;
  onToggle?: () => void;
  /** Extra control rendered below the header, e.g. a DimmerSlider. */
  children?: ReactNode;
  className?: string;
}

export function DeviceTile({
  name,
  icon,
  domain = "neutral",
  state,
  active = false,
  available = true,
  onToggle,
  children,
  className,
}: DeviceTileProps) {
  const interactive = available && !!onToggle;
  return (
    <Tile
      domain={domain}
      active={active && available}
      interactive={interactive}
      aria-label={`${name}: ${available ? state : "unavailable"}`}
      className={cn(!available && "opacity-50", className)}
      onClick={interactive ? onToggle : undefined}
      // Tile deliberately delegates keyboard activation to composers; this is that handler.
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onToggle?.();
              }
            }
          : undefined
      }
    >
      <div className="flex items-start justify-between gap-2">
        <IconBubble domain={domain} filled={active && available}>
          {icon}
        </IconBubble>
        <div className="flex min-w-0 flex-col items-end text-right">
          <span className="w-full truncate text-sm font-medium">{name}</span>
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.span
              key={available ? state : "unavailable"}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: DURATION.fast, ease: EASE_SIGNATURE }}
              className="text-xs text-muted-foreground tabular-nums"
            >
              {available ? state : "Unavailable"}
            </motion.span>
          </AnimatePresence>
        </div>
      </div>
      {available && children}
    </Tile>
  );
}
